import { useCallback, useMemo, useState } from 'react';
import { RiderDto } from '@/features/partner/supplierFee/riderToPartner/types/supplierFee.types';
import { promoMissionApi } from '../api/promoMissionApi';
import { getItem, STORAGE_KEYS } from '@/shared/utils/storage';
import { PartnerPortalLoginResponse } from '@/features/auth/types/auth.types';
import { useToast } from '@/shared/components/feedback/Toast/ToastContext';
import { ToastType } from '@/shared/components/feedback/Toast/Toast.types';
import { Mission, MissionType, MissionStatus, FormState, DEFAULT_FORM } from '../types/promotion.types';

interface UsePromotionFormProps {
    missions: Mission[];
    suppId: string | null;
    isReadOnly: boolean;
    riders: RiderDto[];
    ridersLoaded: boolean;
    fetchRiders: () => Promise<void>;
    fetchMissions: () => Promise<void>;
}

export function usePromotionForm({
    missions,
    suppId,
    isReadOnly,
    ridersLoaded,
    fetchRiders,
    fetchMissions,
}: UsePromotionFormProps) {
    const { showToast } = useToast();

    const [form, setForm] = useState<FormState>(DEFAULT_FORM);
    const [editingSeqNo, setEditingSeqNo] = useState<number | null>(null);
    const [riderSearch, setRiderSearch] = useState('');

    const setField = useCallback(<K extends keyof FormState>(key: K, value: FormState[K]) => {
        setForm((prev) => ({ ...prev, [key]: value }));
    }, []);

    const handleMissionTypeChange = useCallback((code: MissionType) => {
        setForm((prev) => ({ ...prev, missionType: code, rewardAmount: '' }));
    }, []);

    const toggleRider = useCallback((riderId: string) => {
        setForm((prev) => {
            const has = prev.targetRiders.includes(riderId);
            return {
                ...prev,
                targetRiders: has
                    ? prev.targetRiders.filter((id) => id !== riderId)
                    : [...prev.targetRiders, riderId],
            };
        });
    }, []);

    const isValid = useMemo(() => {
        if (!form.title.trim()) return false;
        if (!form.targetCount || parseInt(form.targetCount) <= 0) return false;
        if (!form.rewardAmount || parseInt(form.rewardAmount) <= 0) return false;
        if (!form.startDate || !form.endDate) return false;
        if (form.startDate > form.endDate) return false;
        if (form.targetType === 'SPECIFIC' && form.targetRiders.length === 0) return false;
        return true;
    }, [form]);

    const handleRegister = useCallback(async () => {
        if (!isValid || isReadOnly || !suppId) return;
        const titleTrimmed = form.title.trim();
        if (missions.some((m) => m.title === titleTrimmed)) {
            showToast('이미 동일한 제목의 미션이 존재합니다.', { type: ToastType.ERROR });
            return;
        }
        try {
            const userInfo = getItem<PartnerPortalLoginResponse>(STORAGE_KEYS.USER_INFO);
            await promoMissionApi.registerMission({
                suppId,
                title: titleTrimmed,
                missionType: form.missionType,
                periodType: form.period,
                targetCnt: parseInt(form.targetCount),
                rewardAmt: parseInt(form.rewardAmount),
                maxCnt: parseInt(form.maxCount) || 0,
                startDate: form.startDate,
                endDate: form.endDate,
                description: form.description.trim(),
                targetType: form.targetType,
                targetRiders: form.targetRiders,
                insUser: userInfo?.userId ?? 'UNKNOWN',
            });
            showToast('미션이 등록되었습니다.', { type: ToastType.SUCCESS });
            setForm(DEFAULT_FORM);
            setRiderSearch('');
            await fetchMissions();
        } catch {
            showToast('미션 등록에 실패했습니다.', { type: ToastType.ERROR });
        }
    }, [isValid, isReadOnly, suppId, form, missions, showToast, fetchMissions]);

    const handleReset = useCallback(() => {
        setEditingSeqNo(null);
        setForm(DEFAULT_FORM);
        setRiderSearch('');
    }, []);

    const handleDelete = useCallback(
        async (seqNo: number) => {
            if (!window.confirm('미션을 삭제하시겠습니까?')) return;
            try {
                await promoMissionApi.deleteMission(seqNo);
                showToast('미션이 삭제되었습니다.', { type: ToastType.SUCCESS });
                await fetchMissions();
            } catch {
                showToast('미션 삭제에 실패했습니다.', { type: ToastType.ERROR });
            }
        },
        [showToast, fetchMissions]
    );

    const handleStatusToggle = useCallback(
        async (m: Mission) => {
            const newStatus: MissionStatus = m.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
            const label = newStatus === 'ACTIVE' ? '활성화' : '비활성화';
            if (!window.confirm(`미션을 ${label}하시겠습니까?`)) return;
            try {
                const userInfo = getItem<PartnerPortalLoginResponse>(STORAGE_KEYS.USER_INFO);
                await promoMissionApi.updateMissionStatus(
                    m.seqNo,
                    newStatus,
                    userInfo?.userId ?? 'UNKNOWN'
                );
                showToast(`미션이 ${label}되었습니다.`, { type: ToastType.SUCCESS });
                await fetchMissions();
            } catch {
                showToast(`미션 ${label}에 실패했습니다.`, { type: ToastType.ERROR });
            }
        },
        [showToast, fetchMissions]
    );

    const handleEditStart = useCallback(
        (m: Mission) => {
            setEditingSeqNo(m.seqNo);
            setForm({
                missionType: m.missionType,
                title: m.title,
                period: m.period,
                targetCount: String(m.targetCount),
                rewardAmount: String(m.rewardAmount),
                maxCount: String(m.maxCount),
                startDate: m.startDate,
                endDate: m.endDate,
                description: m.description,
                targetType: m.targetType,
                targetRiders: [...m.targetRiders],
            });
            setRiderSearch('');
            if (m.targetType === 'SPECIFIC' && !ridersLoaded) fetchRiders();
            window.scrollTo({ top: 0, behavior: 'smooth' });
        },
        [ridersLoaded, fetchRiders]
    );

    const handleUpdate = useCallback(async () => {
        if (!isValid || !editingSeqNo) return;
        const titleTrimmed = form.title.trim();
        if (missions.some((m) => m.seqNo !== editingSeqNo && m.title === titleTrimmed)) {
            showToast('이미 동일한 제목의 미션이 존재합니다.', { type: ToastType.ERROR });
            return;
        }
        try {
            const userInfo = getItem<PartnerPortalLoginResponse>(STORAGE_KEYS.USER_INFO);
            await promoMissionApi.updateMission(editingSeqNo, {
                title: titleTrimmed,
                missionType: form.missionType,
                periodType: form.period,
                targetCnt: parseInt(form.targetCount),
                rewardAmt: parseInt(form.rewardAmount),
                maxCnt: parseInt(form.maxCount) || 0,
                startDate: form.startDate,
                endDate: form.endDate,
                description: form.description.trim(),
                targetType: form.targetType,
                targetRiders: form.targetRiders,
                updUser: userInfo?.userId ?? 'UNKNOWN',
            });
            showToast('미션이 수정되었습니다.', { type: ToastType.SUCCESS });
            setEditingSeqNo(null);
            setForm(DEFAULT_FORM);
            setRiderSearch('');
            await fetchMissions();
        } catch {
            showToast('미션 수정에 실패했습니다.', { type: ToastType.ERROR });
        }
    }, [isValid, editingSeqNo, form, missions, showToast, fetchMissions]);

    return {
        form,
        editingSeqNo,
        riderSearch,
        setRiderSearch,
        isValid,
        setField,
        handleMissionTypeChange,
        toggleRider,
        handleRegister,
        handleReset,
        handleDelete,
        handleStatusToggle,
        handleEditStart,
        handleUpdate,
    };
}
